import { useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from "@chakra-ui/react";
import axios from "axios";
import { PropsWithChildren, useState, useEffect, useContext, createContext } from "react";
import { IAddress, getCurrentAddressFromLatLong } from "../service/club/api";
import { useTheme } from "./ThemeProvider";
import { Text } from "@/components/atoms/Text";

interface forcedLocation {
  state: string;
  city: string;
}

interface ILocationContext {
  latitude?: number;
  longitude?: number;
  address?: IAddress;
  isLocated?: boolean;
  hasLocationEnabled?: boolean;
  isLoading: boolean;
  forcedLocation?: forcedLocation | null;
  setForcedLocation?: (location: forcedLocation | null) => void;
}

const defaultLocationContextData: ILocationContext = {
  isLoading: true,
};

const LocationContext = createContext(defaultLocationContextData);

export function LocationProvider({ children }: PropsWithChildren) {
  const [data, setData] = useState(defaultLocationContextData);
  const [forcedLocation, setForcedLocation] = useState<forcedLocation | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { colors, isNative } = useTheme();

  const fetchLocation = () => {
    if (!navigator?.geolocation) {
      console.log('Geolocalização não é suportada pelo seu navegador.');
      return;
    }

    const success = (position: any) => {
      const { latitude, longitude } = position?.coords || {};
      if (latitude && longitude) {
        setData((prevData) => ({
          ...prevData,
          hasLocationEnabled: true,
          latitude: latitude,
          longitude: longitude,
        }));
      }
    };
    const errorHandler = () => {
      setData((prevData) => ({
        ...prevData,
        isLoading: false,
        hasLocationEnabled: false,
        isLocated: false,
      }));
    };
    navigator?.geolocation?.getCurrentPosition(success, errorHandler);
  };

  async function getLatLong(city: string, state: string) {
    if (!city || !state) {
      console.log('Cidade ou estado não fornecidos.');
      return null;
    }
    
    const query = `${city}, ${state}, Brazil`; // Ajuste para o país desejado
    try {
      const response = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: {
          q: query,
          format: 'json',
          limit: 1, // Retorna apenas o primeiro resultado
        },
      });

      if (response?.data?.length > 0) {
        const { lat, lon } = response.data[0] || {};
        if (lat && lon) {
          setData({ ...data, longitude: lon, latitude: lat, isLoading: false, isLocated: true });
        } else {
          console.log('Coordenadas inválidas recebidas.');
          return null;
        }
      } else {
        console.log('Nenhum resultado encontrado.');
        return null;
      }
    } catch (error) {
      console.error('Erro ao buscar coordenadas:', error);
      return null;
    }
  }

  const handlePermissionChange = async () => {
    localStorage.setItem('geolocation_permission', 'true');
    onClose();

    const permissionStatus = await navigator?.permissions?.query({ name: 'geolocation' });

    if (permissionStatus?.state === 'granted') {
      fetchLocation();
      setForcedLocation(null);
    } else if (permissionStatus?.state === 'prompt') {
      fetchLocation();
      setForcedLocation(null);
    } else if (permissionStatus?.state === 'denied') {
      console.log('Permissão negada.');
      fetchLocation();
      setForcedLocation(null);
      //alert('Por favor, habilite a localização nas configurações do seu navegador.');
    }

    navigator?.permissions?.query({ name: 'geolocation' })?.then((permissionStatus) => {
      if (permissionStatus) {
        permissionStatus.onchange = handlePermissionChange;
      }
    });
  };

  useEffect(() => {
    if (!data?.longitude || !data?.latitude || data?.address) return;

    getCurrentAddressFromLatLong(data.latitude, data.longitude).then((address) =>
      setData({ ...data, address: address, isLoading: false, isLocated: true })
    ).catch((error) => {
      console.error('Erro ao obter endereço:', error);
      setData({ ...data, isLoading: false, isLocated: false });
    });
  }, [data]);

  useEffect(() => {
    if (!forcedLocation) {
      if (!localStorage.getItem('geolocation_permission') && !isNative) {
        const legal = window?.location?.pathname?.includes('/legal/') || false;

        if (!legal) {
          setTimeout(() => {
            onOpen();
          }, 3000);
        }
      } else {
        handlePermissionChange();
      }
    } else {
      getLatLong(forcedLocation?.city || '', forcedLocation?.state || '');
    }
  }, [forcedLocation]);

  return (
    <LocationContext.Provider
      value={{
        ...data,
        setForcedLocation,
        forcedLocation,
      }}
    >
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Permissão de Localização</ModalHeader>
          <ModalBody>
            <Text>
              Para melhorar sua experiência, e mostrar promoções perto de você, precisamos acessar sua localização. Você
              pode permitir isso agora?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              backgroundColor={colors?.primaryColor || '#007bff'}
              _hover={{ backgroundColor: colors?.primaryColor || '#0056b3' }}
              onClick={handlePermissionChange}
            >
              Continuar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext() {
  return useContext(LocationContext);
}
